import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface SimulatorStartRequest {
  customer_id: string;
  scenario_id: string;
  scenario_name: string;
  channel: 'whatsapp' | 'sms';
  system_prompt: string;
  customer_context: {
    name: string;
    vehicle_owned?: string;
    purchase_date?: string;
    last_service?: string;
    phone: string;
    email: string;
  };
}

export interface SimulatorStartResponse {
  session_id: string;
  assistant_id: string;
  thread_id: string;
  initial_message: string;
  status: 'active' | 'error';
  created_at: string;
}

export interface SimulatorMessageRequest {
  message: string;
  role?: 'user' | 'assistant';
}

export interface SimulatorMessageResponse {
  message_id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  confidence?: number;
  intent?: string;
  metadata?: {
    tokens_used?: number;
    response_time_ms?: number;
    model?: string;
    handoff_data?: {
      reason: string;
      urgency: string;
      interest_level: string;
    };
    lead_created?: boolean;
  };
}

export interface SimulatorEndResponse {
  session_id: string;
  status: 'completed';
  duration_seconds: number;
  messages_count: number;
  metrics: {
    average_confidence: number;
    average_response_time_ms: number;
    total_tokens_used: number;
    goals_achieved: string[];
  };
}

class SimulatorService {
  private getAuthToken(): string | null {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }

  private getAuthHeaders() {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async startSimulation(request: SimulatorStartRequest): Promise<SimulatorStartResponse> {
    try {
      const response = await axios.post(
        `${API_URL}/v1/simulator/start`,
        request,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to start simulation:', error);
      // Return a fallback response for development
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // If the backend endpoint doesn't exist yet, return mock data
        return {
          session_id: `sim_${Date.now()}`,
          assistant_id: 'asst_mock',
          thread_id: 'thread_mock',
          initial_message: this.generateInitialMessage(request),
          status: 'active',
          created_at: new Date().toISOString(),
        };
      }
      throw error;
    }
  }

  async sendMessage(
    sessionId: string,
    request: SimulatorMessageRequest
  ): Promise<SimulatorMessageResponse> {
    try {
      const response = await axios.post(
        `${API_URL}/v1/simulator/${sessionId}/message`,
        request,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to send message:', error);
      // Return a fallback response for development
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // If the backend endpoint doesn't exist yet, return mock data
        return {
          message_id: `msg_${Date.now()}`,
          role: 'assistant',
          content: this.generateMockResponse(request.message),
          timestamp: new Date().toISOString(),
          confidence: 85 + Math.random() * 10,
          intent: 'response',
          metadata: {
            tokens_used: Math.floor(50 + Math.random() * 100),
            response_time_ms: Math.floor(500 + Math.random() * 1000),
            model: 'gpt-4-turbo',
          },
        };
      }
      throw error;
    }
  }

  async endSimulation(sessionId: string): Promise<SimulatorEndResponse> {
    try {
      const response = await axios.post(
        `${API_URL}/v1/simulator/${sessionId}/end`,
        {},
        {
          headers: this.getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to end simulation:', error);
      // Return a fallback response for development
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return {
          session_id: sessionId,
          status: 'completed',
          duration_seconds: Math.floor(Math.random() * 300) + 60,
          messages_count: Math.floor(Math.random() * 10) + 5,
          metrics: {
            average_confidence: 88.5,
            average_response_time_ms: 750,
            total_tokens_used: 1250,
            goals_achieved: ['Interest assessed', 'Information provided'],
          },
        };
      }
      throw error;
    }
  }

  // Helper method to generate initial message based on scenario
  private generateInitialMessage(request: SimulatorStartRequest): string {
    const { customer_context, scenario_id } = request;
    const name = customer_context.name;
    const vehicle = customer_context.vehicle_owned || 'vehicle';
    const year = customer_context.purchase_date
      ? new Date(customer_context.purchase_date).getFullYear()
      : '2023';

    switch (scenario_id) {
      case 'trade-in':
        return `Hi ${name}, you purchased a ${vehicle} with us back in ${year}. We have some exciting new models that might interest you. Have you thought about upgrading to something newer?`;

      case 'service':
        return `Hi ${name}, our records show your ${vehicle} is due for its annual service. We have some convenient appointment slots available this week. Would you like to schedule your service?`;

      case 'check-in':
        return `Hi ${name}, it's been a while since you got your ${vehicle} from us. We wanted to check in and see how everything is going with your vehicle. How has your experience been so far?`;

      case 'special-offer':
        return `Hi ${name}, as one of our valued customers, you qualify for an exclusive offer on our latest models. We're offering special financing rates and bonus trade-in values this month only. Would you like to hear more?`;

      default:
        return `Hi ${name}, this is Doxa Motors. How can we assist you today?`;
    }
  }

  // Helper method to generate mock responses for development
  private generateMockResponse(userMessage: string): string {
    const message = userMessage.toLowerCase();

    // Trade-in related responses
    if (message.includes('trade') || message.includes('worth') || message.includes('value')) {
      return "Based on your vehicle's age and condition, I can offer you a competitive trade-in value of approximately €15,000-€18,000. This could significantly reduce your monthly payments on a new model. Would you like to see some options?";
    }

    // Service related responses
    if (message.includes('service') || message.includes('appointment') || message.includes('cost')) {
      return "Your annual service would cost approximately €350, which includes oil change, filter replacements, and a comprehensive 50-point inspection. We have slots available this Thursday at 9 AM or Friday at 2 PM. Which works better for you?";
    }

    // Model/vehicle related responses
    if (message.includes('model') || message.includes('electric') || message.includes('new')) {
      return "We have some fantastic new models including the latest electric vehicles with ranges over 500km. The new models come with advanced safety features and our best warranty package. Would you like to schedule a test drive?";
    }

    // Financing related responses
    if (message.includes('finance') || message.includes('payment') || message.includes('rate')) {
      return "We're currently offering special financing at 2.9% APR for qualified customers, plus an additional €2,000 bonus on your trade-in value. Monthly payments would start around €399. Should I prepare a detailed quote for you?";
    }

    // Time/scheduling related responses
    if (message.includes('when') || message.includes('time') || message.includes('available')) {
      return "We have several convenient times available this week. Our showroom is open Monday-Saturday 9 AM to 7 PM. When would be the best time for you to visit? I can also arrange for our mobile service to come to you.";
    }

    // Positive responses
    if (message.includes('yes') || message.includes('interested') || message.includes('tell me more')) {
      return "Excellent! I'm glad you're interested. Let me gather some more details to provide you with the best options. What's most important to you in your next vehicle - fuel efficiency, space, performance, or technology features?";
    }

    // Negative responses
    if (message.includes('no') || message.includes('not interested') || message.includes('happy')) {
      return "I completely understand, and I'm glad you're happy with your current vehicle! Just so you know, we're here whenever you need us for service or if your needs change. Is there anything else I can help you with today?";
    }

    // Default response
    return "That's a great question! Let me provide you with more information. Based on your profile and vehicle history, I can offer you several options that might interest you. Would you prefer to discuss this over a phone call or schedule an in-person consultation?";
  }
}

export const simulatorService = new SimulatorService();